import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { PlusOutlined } from '@ant-design/icons';
import { Popconfirm, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PackageService } from '../../../api/packageService';
import { PackageResponse } from '../../../@type/package.types';
import ActionButton from '../../../components/common/buttons/ActionButton';
import StatusBadge from '../../../components/common/StatusBadge';
import MuiTable from '../../../components/common/MuiTable';
import Card from '../../../components/common/Card';
import { Column } from '../../../utils/constants';
import { formatNumber } from '../../../utils/utils';

const PackageList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await PackageService.getAllPackages();
      if (response?.data?.data) {
        // Map data to include action field (required by MuiTable)
        const mappedData = response.data.data.map((pkg: PackageResponse) => ({
          ...pkg,
          action: pkg.id, // Action column needs the ID
        }));
        setPackages(mappedData);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error('Failed to load packages:', error);
      setPackages([]);
      messageApi.open({
        type: 'error',
        content: 'Failed to load packages',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoadingDelete(true);
      await PackageService.deletePackage(id);
      messageApi.open({
        type: 'success',
        content: t('packages.deleteSuccess'),
      });
      // Reload packages
      await loadPackages();
    } catch (error) {
      console.error('Failed to delete package:', error);
      messageApi.open({
        type: 'error',
        content: 'Failed to delete package',
      });
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleEdit = (id: number) => {
    navigate(`/settings/packages/${id}/edit`);
  };

  const handleConfigureMenus = (id: number) => {
    navigate(`/settings/packages/${id}/menus`);
  };

  // Define table columns
  const header: Column[] = [
    {
      id: 'code',
      label: t('packages.code'),
      minWidth: 120,
      align: 'left',
      format: (value: string) => {
        return <div className="font-bold">{value}</div>;
      },
    },
    {
      id: 'name',
      label: t('packages.name'),
      minWidth: 150,
      align: 'left',
      format: (value: string) => {
        return <div className="font-semibold">{value}</div>;
      },
    },
    {
      id: 'price',
      label: t('packages.price'),
      minWidth: 100,
      align: 'center',
      format: (value: number, row?: PackageResponse) => {
        const currency = row?.pricing_strategy === 'per_country' ? '' : row?.default_currency;
        return (
          <div className="font-bold">
            {formatNumber(value, 2)}
            {currency ? ` ${currency}` : ''}
          </div>
        );
      },
    },
    {
      id: 'billing_period',
      label: t('packages.billingPeriod'),
      minWidth: 120,
      align: 'center',
      format: (value: string) => {
        return (
          <Chip
            label={t(`packages.${value}`)}
            size="small"
            className="bg-blue-100 text-blue-800"
          />
        );
      },
    },
    {
      id: 'pricing_strategy',
      label: t('packages.pricingStrategy'),
      minWidth: 150,
      align: 'center',
      format: (value: string) => {
        const isGlobal = value === 'global';
        return (
          <Chip
            label={isGlobal ? t('packages.globalPricing') : t('packages.perCountryPricing')}
            size="small"
            color={isGlobal ? 'primary' : 'secondary'}
            className={isGlobal ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}
          />
        );
      },
    },
    {
      id: 'max_staff',
      label: t('packages.maxStaff'),
      minWidth: 100,
      align: 'center',
      format: (value: number) => {
        return <div className="font-bold">{value || 'Unlimited'}</div>;
      },
    },
    {
      id: 'is_active',
      label: t('packages.status'),
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => {
        return (
          <StatusBadge
            label={value ? t('packages.active') : t('packages.inactive')}
            tone={value ? 'success' : 'error'}
          />
        );
      },
    },
    {
      id: 'action',
      label: t('common.table.actions'),
      minWidth: 150,
      align: 'center',
      format: (id: number) => {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleConfigureMenus(id)}
              title={t('packages.assignMenus')}
              disabled={loadingDelete}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="success"
              onClick={() => handleEdit(id)}
              disabled={loadingDelete}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Popconfirm
              title={t('packages.deleteConfirm')}
              description={t('packages.deleteWarning')}
              okText={t('btn.confirm')}
              cancelText={t('btn.cancel')}
              onConfirm={() => handleDelete(id)}
              okButtonProps={{
                style: { background: '#ba2156', border: 'none' },
                loading: loadingDelete,
              }}
            >
              <IconButton size="small" color="error" disabled={loadingDelete}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">{t('packages.title')}</h1>
            <ActionButton
              startIcon={<PlusOutlined />}
              onClick={() => navigate('/settings/packages/create')}
            >
              {t('packages.create')}
            </ActionButton>
          </div>

          {/* Table */}
          <MuiTable header={header} data={packages} isLoading={loading} />
        </div>
      </Card>
    </>
  );
};

export default PackageList;
